/**
 * Popup exibido quando o usuário (quem está cobrando) recebe um pagamento na blockchain.
 * Se a taxa estiver habilitada, calcula a porção que o cobrador deve pagar (com base
 * no absorbTransferFee salvo ao gerar o QR) e oferece "Pagar Taxa" ou "Pagar depois".
 */

import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Loader2, Send } from 'lucide-react';
import { BrowserProvider, Contract, parseUnits } from 'ethers';
import Modal from '@/components/ui/Modal';
import { IncomingTransferContext } from '@/contexts/IncomingTransferContext';
import { usePendingFees } from '@/contexts/PendingFeeContext';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { ERC20_TRANSFER_ABI } from '@/config/stablecoinContracts';
import { formatCurrencyDisplay } from '@/utils/currency';
import { formatAddress, getAddressOrWalletName } from '@/utils/formatters';
import { getBlockExplorerTxUrl } from '@/utils/blockExplorer';
import { useWallets } from '@/contexts/WalletContext';
import { useOpenWalletList } from '@/contexts/OpenWalletListContext';
import { hasShownAddToListPrompt, markAddToListPromptShown } from '@/utils/addToListPromptSession';

// Feature flag: convite "Adicionar à lista de carteiras" a partir de movimentações.
// Mantido para possível reativação futura; por enquanto o link fica oculto.
const SHOW_ADD_TO_WALLET_FROM_MOVEMENTS = false;

/** Lê o absorbTransferFee salvo pelo TransferForm ao gerar QR. Retrocompat: aceita billerAbsorbPercent 100 = true. */
function getLastAbsorbTransferFee() {
  try {
    const raw = localStorage.getItem('pcp_last_biller_absorb');
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    if (typeof parsed.absorbTransferFee === 'boolean') return parsed.absorbTransferFee;
    if (typeof parsed.billerAbsorbPercent === 'number') return parsed.billerAbsorbPercent === 100;
    return false;
  } catch {
    return false;
  }
}

export default function IncomingTransferReceivedModal() {
  const { t } = useTranslation();
  const { incomingTransfer, clearIncomingTransfer } = useContext(IncomingTransferContext) || {};
  const {
    calculateFeeFromReceived,
    addPendingFee,
    getFeeAddress,
    feeEnabled,
    feePercent,
    feeBillingPercent,
  } = usePendingFees();
  const { provider: metamaskProvider } = useWalletConnection();
  const { wallets } = useWallets();
  const { openWalletListWithAddress } = useOpenWalletList();

  const [paying, setPaying] = useState(false);
  const [feePaid, setFeePaid] = useState(false);
  const [error, setError] = useState(null);

  // absorbTransferFee definido pelo cobrador ao gerar o QR (Configurações)
  const absorbTransferFee = useMemo(() => {
    if (!incomingTransfer) return false;
    return getLastAbsorbTransferFee();
  }, [incomingTransfer]);

  const totalFeePercent = feePercent + feeBillingPercent;

  // Taxa que o cobrador deve pagar (taxa de recebimento sempre + taxa de transferência se absorveu)
  const feeUSD = useMemo(() => {
    if (!incomingTransfer || !feeEnabled) return 0;
    return calculateFeeFromReceived(incomingTransfer.amount, absorbTransferFee);
  }, [incomingTransfer, feeEnabled, calculateFeeFromReceived, absorbTransferFee]);

  const feeAddress = incomingTransfer ? getFeeAddress(incomingTransfer.chainId) : null;
  const hasFee = feeEnabled && feeUSD > 0 && !!feeAddress;
  // Sempre adicionar taxa como pendente; não solicitar pagamento no recebimento (config "askFeePaymentOnReceive" oculta por enquanto)
  const showFeeOption = false;

  const addedPendingForTxRef = useRef(null);
  // Sempre adiciona a taxa como pendente ao abrir o modal (uma vez por tx)
  useEffect(() => {
    if (!incomingTransfer?.txHash || !hasFee) return;
    if (addedPendingForTxRef.current === incomingTransfer.txHash) return;
    addedPendingForTxRef.current = incomingTransfer.txHash;
    addPendingFee({
      feeUSD,
      token: incomingTransfer.symbol,
      chainId: incomingTransfer.chainId,
      networkName: incomingTransfer.networkName,
      originTxHash: incomingTransfer.txHash,
    });
  }, [incomingTransfer?.txHash, hasFee, feeUSD, addPendingFee]);

  if (!incomingTransfer) return null;

  const { amount, symbol, txHash, networkName, chainId, tokenAddress, fromAddress, receivedAt } = incomingTransfer;
  const payerDisplay = getAddressOrWalletName(wallets, fromAddress, 6, 4);
  const isSenderInList = !!fromAddress && wallets.some((w) => w?.address && String(w.address).toLowerCase() === String(fromAddress).toLowerCase());
  const showAddToWalletList =
    SHOW_ADD_TO_WALLET_FROM_MOVEMENTS &&
    !!fromAddress &&
    !isSenderInList &&
    openWalletListWithAddress &&
    !hasShownAddToListPrompt(fromAddress);

  /** Paga a taxa imediatamente ao FEE_ADDRESS. */
  const handlePayNow = async () => {
    if (!metamaskProvider || !feeAddress || !tokenAddress) return;
    setPaying(true);
    setError(null);

    try {
      const ethersProvider = new BrowserProvider(metamaskProvider);
      const signer = await ethersProvider.getSigner();
      const contract = new Contract(tokenAddress, ERC20_TRANSFER_ABI, signer);

      let decimals = 6;
      try { decimals = Number(await contract.decimals()); } catch { /* padrão */ }

      const amountStr = feeUSD.toFixed(decimals);
      const amountUnits = parseUnits(amountStr, decimals);

      const tx = await contract.transfer(feeAddress, amountUnits);
      await tx.wait();

      setFeePaid(true);
    } catch (err) {
      console.error('[IncomingTransfer] Erro ao pagar taxa:', err);
      if (err?.code === 'ACTION_REJECTED' || err?.code === 4001) {
        setError(t('modal.paymentCancelled'));
      } else {
        setError(err?.message || t('modal.payFeeError'));
      }
    } finally {
      setPaying(false);
    }
  };

  /** Fecha o modal. Se a opção de pagar foi exibida e o usuário não pagou, adiciona como pendente. */
  const handleClose = () => {
    if (showFeeOption && hasFee && !feePaid) {
      addPendingFee({
        feeUSD,
        token: symbol,
        chainId,
        networkName,
        originTxHash: txHash,
      });
    }
    setFeePaid(false);
    setError(null);
    clearIncomingTransfer?.();
  };

  return (
    <Modal
      isOpen={!!incomingTransfer}
      onClose={handleClose}
      title={t('modal.paymentReceived')}
    >
      <div className="flex flex-col items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-green-500/20 flex items-center justify-center">
          <CheckCircle size={32} className="text-green-400" />
        </div>

        <p className="text-gray-300 text-center">{t('modal.youReceived')}</p>

        <p className="text-2xl font-bold text-white">
          {formatCurrencyDisplay(typeof amount === 'number' ? amount : parseFloat(amount), 'USD')} {symbol}
        </p>

        {(receivedAt || fromAddress) && (
          <div className="text-sm text-center">
            <p className="text-green-400">
              {receivedAt
                ? new Date(receivedAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                : ''}
              {receivedAt && fromAddress ? ' - ' : ''}
              {fromAddress ? formatAddress(fromAddress, 6, 4) : ''}
            </p>
            {payerDisplay.isRegistered && payerDisplay.display && (
              <p className="text-white mt-0.5">{t('modal.payerLabel', { name: payerDisplay.display })}</p>
            )}
            {showAddToWalletList && (
              <p className="mt-2">
                <button
                  type="button"
                  onClick={() => {
                  markAddToListPromptShown(fromAddress);
                  openWalletListWithAddress(fromAddress);
                }}
                  className="text-purple-400 hover:text-purple-300 underline text-sm"
                >
                  {t('common.addToWalletList')}
                </button>
              </p>
            )}
          </div>
        )}

        {networkName && (
          <p className="text-sm text-gray-400">{t('header.network')}: {networkName}</p>
        )}

        {txHash && (
          <p className="text-xs text-gray-500 text-center mt-1">
            {getBlockExplorerTxUrl(chainId, txHash) ? (
              <a
                href={getBlockExplorerTxUrl(chainId, txHash)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-purple-400 hover:text-purple-300 underline"
              >
                {t('transfer.viewOnBlockExplorer')}
              </a>
            ) : (
              <span className="break-all">{t('transfer.hash')} {txHash}</span>
            )}
          </p>
        )}

        {/* Quando não solicita pagamento da taxa, informa que foi registrada como pendente */}
        {hasFee && !showFeeOption && (
          <p className="text-xs text-gray-500 text-center mt-2">
            {t('modal.feeRegisteredPending', { amount: formatCurrencyDisplay(feeUSD, 'USD') })}
          </p>
        )}

        {/* Seção de taxa (só quando a configuração pede para solicitar pagamento) */}
        {showFeeOption && !feePaid && (
          <div className="w-full bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mt-2">
            <p className="text-sm text-yellow-300 text-center mb-1">
              {t('modal.feesApplyYouAbsorb', { percent: totalFeePercent, absorb: absorbTransferFee ? t('modal.absorbYes') : t('modal.absorbNo') })}
            </p>
            <p className="text-lg font-bold text-white text-center">
              {t('modal.yourPart', { amount: formatCurrencyDisplay(feeUSD, 'USD'), symbol })}
            </p>
            <p className="text-xs text-gray-400 text-center mt-1">
              {t('modal.payNowOrPending')}
            </p>
          </div>
        )}

        {feePaid && (
          <div className="w-full bg-green-500/10 border border-green-500/30 rounded-xl p-3">
            <p className="text-sm text-green-400 text-center font-medium">
              {t('modal.feePaidSuccess', { amount: formatCurrencyDisplay(feeUSD, 'USD') })}
            </p>
          </div>
        )}

        {error && (
          <p className="text-red-400 text-sm text-center">{error}</p>
        )}

        {/* Botões */}
        {showFeeOption && !feePaid ? (
          <>
            <button
              type="button"
              disabled={paying}
              onClick={handlePayNow}
              className="w-full py-3 px-4 bg-yellow-600 hover:bg-yellow-700 disabled:opacity-50 text-white font-medium rounded-xl transition flex items-center justify-center gap-2"
            >
              {paying ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {t('modal.payingFee')}
                </>
              ) : (
                <>
                  <Send size={18} />
                  {t('modal.payFeeAmount', { amount: formatCurrencyDisplay(feeUSD, 'USD') })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={paying}
              className="w-full py-2 text-sm text-gray-400 hover:text-white transition"
            >
              {t('modal.payLater')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={handleClose}
            className="w-full mt-2 py-3 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-xl transition"
          >
            {t('common.close')}
          </button>
        )}
      </div>
    </Modal>
  );
}
